import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_DARK_GRADIENT } from '../../constants/theme';
import { useLanguageContext } from '../../contexts/LanguageContext';
import theFreaksGuideTranslations from './theFreaksGuideTranslations';
import { THE_FREAKS_DEMO_PATH } from './theFreaksDemoScript';

export const THE_FREAKS_GUIDE_PATH = '/the-freaks-guide';
const LOGIN_TO_PLAY = '/login?redirectTo=/the-freaks';

const TheFreaksGuideView = () => {
  const navigate = useNavigate();
  const { language, handleChangeLanguage } = useLanguageContext();
  const t = theFreaksGuideTranslations[language] || theFreaksGuideTranslations.en;

  const ctaButtons = (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
      <Button
        size="large"
        variant="contained"
        onClick={() => navigate('/register')}
        sx={{ background: DEFAULT_GRADIENT, color: 'white', px: 4, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
      >
        {t.register}
      </Button>
      <Button
        size="large"
        variant="outlined"
        onClick={() => navigate(LOGIN_TO_PLAY)}
        sx={{ borderColor: DEFAULT_THEME_COLOR, color: DEFAULT_THEME_COLOR, px: 4, '&:hover': { backgroundColor: 'rgba(54,201,162,0.1)' } }}
      >
        {t.login}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ background: DEFAULT_DARK_GRADIENT, minHeight: '100vh', color: 'white', px: 2, pb: 8 }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/about')} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {t.back}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['en', 'pl'].map((lang) => (
            <Button
              key={lang}
              size="small"
              variant={language === lang ? 'contained' : 'outlined'}
              onClick={() => handleChangeLanguage(lang)}
              sx={{
                minWidth: 44,
                color: language === lang ? 'white' : DEFAULT_THEME_COLOR,
                backgroundColor: language === lang ? DEFAULT_THEME_COLOR : 'transparent',
                borderColor: DEFAULT_THEME_COLOR,
                '&:hover': { backgroundColor: DEFAULT_THEME_COLOR, color: 'white' },
              }}
            >
              {lang.toUpperCase()}
            </Button>
          ))}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center', pt: { xs: 5, md: 8 }, pb: 5 }}>
        <Box sx={{ fontSize: { xs: '3rem', md: '4rem' }, mb: 1 }}>🥊</Box>
        <Chip
          label={t.badge}
          size="small"
          sx={{ mb: 2, backgroundColor: `${DEFAULT_THEME_COLOR}22`, color: DEFAULT_THEME_COLOR, fontWeight: 700, border: `1px solid ${DEFAULT_THEME_COLOR}44` }}
        />
        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.2rem', md: '3rem' } }}>
          {t.title}
        </Typography>
        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 400, mb: 4 }}>
          {t.subline}
        </Typography>
        {ctaButtons}
        <Button
          startIcon={<PlayCircleOutlineIcon />}
          onClick={() => navigate(THE_FREAKS_DEMO_PATH)}
          sx={{ mt: 2, minHeight: 44, color: 'rgba(255,255,255,0.85)', textTransform: 'none', fontSize: '1rem' }}
        >
          {t.watchDemo}
        </Button>
      </Box>

      <Box sx={{ maxWidth: 960, mx: 'auto' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, textAlign: 'center', mb: 3 }}>
          {t.stepsHeading}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          {t.steps.map((step) => (
            <Box
              key={step.title}
              sx={{ p: 3, borderRadius: 3, background: 'rgba(255,255,255,0.04)', border: `1px solid ${DEFAULT_THEME_COLOR}33` }}
            >
              <Box sx={{ fontSize: '2rem', mb: 1 }}>{step.emoji}</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{step.title}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>{step.desc}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3, textAlign: 'center', background: `${DEFAULT_THEME_COLOR}14`, border: `1px solid ${DEFAULT_THEME_COLOR}44` }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>🏆 {t.prizesHeading}</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t.prizes}</Typography>
        </Box>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1 }}>{t.ctaHeading}</Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>{t.ctaSubline}</Typography>
          {ctaButtons}
        </Box>
      </Box>
    </Box>
  );
};

export default TheFreaksGuideView;
